import React, { useState, useEffect } from "react";
import { usePageLoadingContext } from "../contexts/PageLoadingContext";
import { useSelector } from "react-redux";
import store, { RootState } from "../store";
import { updateQuote } from "../store/quote/action";
import { useApi } from "../api";
import {
  BusinessKeywordModel,
  BUSINESS_KEYWORD_RESPONSE,
  InitBusinessKeywordModel,
} from "../api/types/business-keyword-model";
import { BusinessKeywordAutoComplete } from "../components/business-keyword-autocomplete";
import { API_ERROR_MESSAGE } from "../api/launchpad-api";
import { SaveQuoteRequest } from "../api/types";
import {
  CarrierRecommendationModel,
  InitCarrierRecommendationModel,
  RecommendedCarrier,
  InitMultipleCarrierRecommendationModel
} from "../api/types/carrier-recommendation-model";
import { PopupModal } from "../components/popup-modal";

interface BusinessKeywordUkwComponentProps {
  showAlert?: boolean;
  setShowAlert?: (show: boolean) => void;
  alertMessage?: { title: string; message: string };
  setAlertMessage?: (message: { title: string; message: string }) => void;
}

export const BusinessKeywordUkwComponent: React.FC<
  BusinessKeywordUkwComponentProps
> = ({
  showAlert = false,
  setShowAlert = () => {},
  alertMessage = { title: "", message: "" },
  setAlertMessage = () => {},
}) => {
  const api = useApi();
  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState<{
    title: string;
    message: React.ReactNode;
  }>({ title: "", message: "" });
  const quote = useSelector((state: RootState) => state.quote.quote);
  const { showLoader, hideLoader } = usePageLoadingContext();
  const [keywordError, setKeywordError] = useState("");
  const [primaryKeywordList, setPrimaryKeywordList] = useState<
    BusinessKeywordModel[]
  >([]);
  const [secondaryKeywordList, setSecondaryKeywordList] = useState<
    BusinessKeywordModel[]
  >([]);
  const [selectedPrimaryKeyword, setSelectedPrimaryKeyword] =
    useState<BusinessKeywordModel | null>(InitBusinessKeywordModel);
  const [selectedSecondaryKeyword, setSelectedSecondaryKeyword] =
    useState<BusinessKeywordModel | null>(InitBusinessKeywordModel);
  const [carrierRecommendation, setCarrierRecommendation] =
    useState<CarrierRecommendationModel>(InitCarrierRecommendationModel);
  const [universalKeyPairId, setUniversalKeyPairId] = useState<string>("");
  const [multipleCarrierRecommendation, setMultipleCarrierRecommendation] =
    useState<RecommendedCarrier[]>([]);
  const [selectedPartnerIndex, setSelectedPartnerIndex] = useState(-1);
  const [modalFooter, setModalFooter] = useState<React.ReactNode>(null);
  const [onModalClose, setOnModalClose] = useState<() => void>(() => () => {});

  useEffect(() => {
    showLoader();
    api.commercialAgentApi.getPrimaryBusinessKeywords().then(
      (result) => {
        if (result.type === BUSINESS_KEYWORD_RESPONSE) {
          setPrimaryKeywordList(result.data);
        } else {
          window.scrollTo(0, 0);
          setShowAlert(true);
          setAlertMessage({
            title: "Thank you for your request.",
            message: API_ERROR_MESSAGE,
          });
        }
        setShowAlert(false);
        hideLoader();
      },
      (error) => {
        console.error(error);
        hideLoader();
      }
    );
  }, []);

  useEffect(() => {
    if (selectedPrimaryKeyword && selectedPrimaryKeyword.id !== "") {
      showLoader();
      api.commercialAgentApi
        .getSecondaryBusinessKeywords(selectedPrimaryKeyword.id)
        .then(
          (result) => {
            if (result.type === BUSINESS_KEYWORD_RESPONSE) {
              setSecondaryKeywordList(result.data);
              setUniversalKeyPairId(result.data[0].id);
            } else {
              window.scrollTo(0, 0);
              setShowAlert(true);
              setAlertMessage({
                title: "Thank you for your request.",
                message: API_ERROR_MESSAGE,
              });
            }
            setShowAlert(false);
            if (result.data.length > 1) {
              hideLoader();
            }
          },
          (error) => {
            console.error(error);
            hideLoader();
          }
        );
    } else {
      setSecondaryKeywordList([]);
    }
  }, [selectedPrimaryKeyword]);

  useEffect(() => {
    if (
      selectedPrimaryKeyword &&
      selectedPrimaryKeyword.id !== "" &&
      secondaryKeywordList.length > 0 &&
      secondaryKeywordList[0].description == null
    ) {
      setSelectedSecondaryKeyword({
        ...selectedPrimaryKeyword,
        id: universalKeyPairId,
      });
    }
  }, [secondaryKeywordList]);

  useEffect(() => {
    if (selectedSecondaryKeyword && selectedSecondaryKeyword.id !== "") {
      setSelectedPartnerIndex(-1);
      setCarrierRecommendation(InitCarrierRecommendationModel);

      let saveQuote: SaveQuoteRequest = {
        ...quote.data,
        BusinessKeyword: selectedPrimaryKeyword,
        BusinessSecondaryKeyword: selectedSecondaryKeyword,
        CarrierDetail: [],
      };
      store.dispatch(updateQuote(saveQuote));
    } else {
      setMultipleCarrierRecommendation(InitMultipleCarrierRecommendationModel);
    }
  }, [selectedSecondaryKeyword]);

  return (
 
    <div>

      <PopupModal
        modalId="alert-modal"
        showModal={showRedirect}
        onClosed={() => {
          if (onModalClose) onModalClose();
        }}
        modalType="dialogueBox"
        autoShow={false}
        overlayShouldCloseModal={true}
        showCloseButton={true}
        modalHeadline={redirectMessage.title}
        modalContent={redirectMessage.message}
        isBackNav={false}
      >
        {modalFooter}
      </PopupModal>

      <div>
        <BusinessKeywordAutoComplete
          disabled={
            !quote.data.Zip ||
            quote.data.Zip.length !== 5 ||
            !quote.data.StateCode
          }
          label="Tell us what type of business you spend the most time doing."
          placeholder="Example(s) contractor, painter etc"
          type="text"
          defaultValue={""}
          suggestions={primaryKeywordList}
          onItemChange={(selectedItem) => {
            setKeywordError("");
            setSelectedPrimaryKeyword(selectedItem);
            setSelectedSecondaryKeyword(InitBusinessKeywordModel);
          }}
          error={keywordError}
          className="w-full"
          containerClass=""
          suggestionsContainerClass=""
        />
      </div>
      {selectedPrimaryKeyword &&
        selectedPrimaryKeyword.id &&
        secondaryKeywordList.length > 0 &&
        secondaryKeywordList[0].description !== null && (
          <div style={{ marginTop: "3.5rem" }}>
            <BusinessKeywordAutoComplete
              disabled={
                selectedPrimaryKeyword.id === "" ||
                !quote.data.Zip ||
                quote.data.Zip.length !== 5 ||
                !quote.data.StateCode
              }
              label="Please give us a little more information about what your business does."
              placeholder="Example(s) Non Residential, Industrial etc"
              type="text"
              defaultValue={quote.data.BusinessKeyword?.id ?? ""}
              suggestions={secondaryKeywordList}
              onItemChange={(selectedItem) => {
                setKeywordError("");
                setSelectedSecondaryKeyword(selectedItem);
              }}
              error={keywordError}
              className="w-full"
              containerClass=""
              suggestionsContainerClass=""
            />
          </div>
        )}
    </div>
  );
};
